import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Card, Table, Button, Badge, Alert, Spinner, Pagination } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { ordersAPI } from '../utils/api';
import { useAuth } from '../context/AuthContext';

const FulfilledOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  // Ref to prevent duplicate API calls
  const hasLoadedRef = useRef(false);

  const { user } = useAuth();

  useEffect(() => {
    if (!hasLoadedRef.current && user) {
      hasLoadedRef.current = true;
      loadFulfilledOrders();
    }
  }, [user]);

  const loadFulfilledOrders = async (page = currentPage) => {
    try {
      setLoading(true);
      setError('');

      const params = {
        page,
        limit: itemsPerPage,
        fulfillmentStatus: 'fulfilled',
        lightweight: 'super' // Request super lightweight mode
      };

      const res = await ordersAPI.getAll(params);
      setOrders(res.data.data);
      setTotalCount(res.data.totalCount || res.data.count);
      setTotalPages(res.data.totalPages || 1);
    } catch (err) {
      setError(err.response?.data?.message || 'เกิดข้อผิดพลาดในการโหลดข้อมูล');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('th-TH', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('th-TH', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('th-TH', {
      style: 'currency',
      currency: 'THB'
    }).format(amount);
  };

  // Pagination logic
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    loadFulfilledOrders(pageNumber);
  };

  return (
    <Container fluid className="main-content">
      <Row className="mb-4">
        <Col>
          <h2>
            <i className="bi bi-check-circle me-2"></i>
            คำสั่งซื้อที่เบิกแล้ว
          </h2>
          <p className="text-muted">
            ประวัติคำสั่งซื้อที่ดำเนินการเบิกสินค้าเสร็จสมบูรณ์
          </p>
        </Col>
      </Row>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Card>
        <Card.Header className="bg-success bg-opacity-10">
          <Row>
            <Col>
              <h5 className="mb-0">
                <i className="bi bi-box-seam-fill me-2"></i>
                คำสั่งซื้อที่เบิกเสร็จแล้ว ({totalCount} รายการ)
              </h5>
            </Col>
          </Row>
        </Card.Header>
        <Card.Body className="p-0">
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-3 text-muted">กำลังโหลดข้อมูล...</p>
            </div>
          ) : orders.length === 0 ? (
            <div className="text-center py-5">
              <i className="bi bi-inbox" style={{ fontSize: '3rem', color: '#6c757d' }}></i>
              <p className="text-muted mt-3">ยังไม่มีคำสั่งซื้อที่เบิกเสร็จ</p>
            </div>
          ) : (
            <>
              <Table responsive hover className="mb-0">
                <thead className="table-light">
                  <tr>
                    <th>รหัสคำสั่งซื้อ</th>
                    <th>สาขา</th>
                    <th>วันที่สั่ง</th>
                    <th className="text-center">จำนวนรายการ</th>
                    <th className="text-end">มูลค่ารวม</th>
                    <th>ผู้เบิก</th>
                    <th>วันที่เบิกเสร็จ</th>
                    <th className="text-center">การจัดการ</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map(order => (
                    <tr key={order.id}>
                      <td>
                        <strong>#{order.id}</strong>
                      </td>
                      <td>
                        <i className="bi bi-building me-1"></i>
                        {order.orderedBy?.fullName || '-'}
                      </td>
                      <td>
                        <i className="bi bi-calendar me-1"></i>
                        {formatDate(order.createdAt)}
                      </td>
                      <td className="text-center">
                        <Badge bg="secondary">{order.quantity || 0} รายการ</Badge>
                      </td>
                      <td className="text-end">
                        <strong>{formatCurrency(order.totalCost || 0)}</strong>
                      </td>
                      <td>
                        <i className="bi bi-person-check me-1"></i>
                        {order.fulfillment?.fulfilledBy?.fullName || '-'}
                      </td>
                      <td>
                        <i className="bi bi-clock-history me-1"></i>
                        {formatDateTime(order.fulfillment?.completedAt)}
                      </td>
                      <td className="text-center">
                        <Link to={`/orders/${order.id}`}>
                          <Button variant="info" size="sm" title="ดูรายละเอียด">
                            <i className="bi bi-eye"></i>
                          </Button>
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="d-flex justify-content-between align-items-center p-3">
                  <div className="text-muted">
                    แสดง {((currentPage - 1) * itemsPerPage) + 1} - {Math.min(currentPage * itemsPerPage, totalCount)} จาก {totalCount} รายการ
                  </div>
                  <Pagination className="mb-0">
                    <Pagination.First onClick={() => handlePageChange(1)} disabled={currentPage === 1} />
                    <Pagination.Prev onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} />

                    {[...Array(totalPages)].map((_, index) => {
                      const pageNumber = index + 1;
                      // Show first page, last page, current page, and pages around current
                      if (
                        pageNumber === 1 ||
                        pageNumber === totalPages ||
                        (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1)
                      ) {
                        return (
                          <Pagination.Item
                            key={pageNumber}
                            active={pageNumber === currentPage}
                            onClick={() => handlePageChange(pageNumber)}
                          >
                            {pageNumber}
                          </Pagination.Item>
                        );
                      } else if (pageNumber === currentPage - 2 || pageNumber === currentPage + 2) {
                        return <Pagination.Ellipsis key={pageNumber} disabled />;
                      }
                      return null;
                    })}

                    <Pagination.Next onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} />
                    <Pagination.Last onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages} />
                  </Pagination>
                </div>
              )}
            </>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
};

export default FulfilledOrders;
